import React, { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { Sparkles, Send, Loader2 } from "lucide-react";

const SUGGESTIONS = [
  "Do you deliver to my country?",
  "How do I get a refund?",
  "What is SHOP+ membership?",
  "How do I track my order?",
];

function sessionId() {
  let s = sessionStorage.getItem("sn_support_sid");
  if (!s) { s = (crypto.randomUUID ? crypto.randomUUID() : `sid-${Date.now()}-${Math.random().toString(36).slice(2)}`); sessionStorage.setItem("sn_support_sid", s); }
  return s;
}

export function SupportBot() {
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi! 👋 I'm the SHOP NOW Help Assistant. Ask me about delivery, refunds, payments, SHOP+, or how anything works." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(sessionId());
  const listRef = useRef(null);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages, busy]);

  const ask = async (question) => {
    const q = (question || "").trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/support/ask", { session_id: sid.current, question: q });
      setMessages((m) => [...m, { role: "bot", text: data.answer }]);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Sorry — the help assistant is unavailable right now. Please use the form below to submit a case and our team will help. 🙏" }]);
    }
    setBusy(false);
  };

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden mb-6" data-testid="support-bot">
      <div className="flex items-center gap-2.5 px-5 py-3.5 sn-ink-bg text-white">
        <span className="h-8 w-8 rounded-lg bg-emerald grid place-items-center"><Sparkles size={16} /></span>
        <div>
          <div className="font-display font-bold text-sm">Ask SHOP NOW — Instant Help</div>
          <div className="text-[11px] text-white/60">Powered by Claude AI · answers from our policies & catalogue</div>
        </div>
      </div>

      <div ref={listRef} className="max-h-80 overflow-y-auto px-4 py-4 space-y-3" data-testid="support-bot-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-line ${m.role === "user" ? "bg-ink text-white rounded-br-sm" : "bg-secondary text-ink rounded-bl-sm"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start"><div className="bg-secondary text-muted-foreground rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Thinking…</div></div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="px-4 pb-3 flex flex-wrap gap-2" data-testid="support-bot-suggestions">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => ask(s)} className="text-xs font-medium border border-border rounded-full px-3 py-1.5 hover:bg-secondary transition-colors" data-testid="support-bot-suggestion">{s}</button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="flex items-center gap-2 border-t border-border p-3">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your question…" className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm outline-none focus:border-emerald" data-testid="support-bot-input" />
        <button type="submit" disabled={busy || !input.trim()} className="h-10 w-10 grid place-items-center rounded-xl bg-emerald text-white disabled:opacity-50" data-testid="support-bot-send">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
}
